import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { api } from "../../../data";
import { loadUnreadByOrg, type UnreadData, type UnreadScope } from "./data";
import { useUnreadData } from "./hooks";
import { EMPTY_COUNT, alertExcerpt, isNewMessage, personName, type AlertState, type TalentLite, type UnreadCount } from "./model";
import { fadenOffen, zeigeChatHinweis } from "./hinweis";
import { t } from "../../../lib/sprache";

/**
 * Zähler für die Glocke im Seitenrahmen (AD-201, TA-160): ungelesene Meldungen plus ungelesene Nachrichten der
 * angemeldeten Person in der gewählten Agentur. Der Hauptentwickler hängt ihn an `bell.unread` in src/app/layouts.tsx.
 *
 * Nebenbei meldet der Hook eine neue Nachricht (TA-116, AD-185, K-524): Ist das Portal sichtbar, erscheint ein kurzer
 * Hinweis im Portal (Absender, Anfang des Texts, Klick öffnet den Faden), außer die Person hat genau diesen Faden vor sich.
 * Liegt das Portal im Hintergrund, kommt der Desktop-Hinweis: nur mit Erlaubnis des Browsers und nur, wenn die Person ihn
 * nicht abgeschaltet hat (user_preferences.desktop_notifications, Standard an). Der Hook muss unterhalb des Routers laufen.
 */
export function useUnreadCount(scope: UnreadScope | null): UnreadCount {
  const q = useUnreadData(scope);
  useDesktopAlerts(scope, q.data);
  return q.data?.count ?? EMPTY_COUNT;
}

/** Agentur-Portal: `const unread = useAgencyUnreadCount(ws.org.org_id)` → `bell={{ href: "/agentur/benachrichtigungen", unread: unread.total }}`. */
export function useAgencyUnreadCount(orgId: string | null | undefined): UnreadCount {
  return useUnreadCount(orgId ? { portal: "agentur", orgId } : null);
}

/** Creator-Portal: `useCreatorUnreadCount(ws.link.talent_id, ws.link.org_id)`; zählt nur die gewählte Agentur. */
export function useCreatorUnreadCount(talentId: string | null | undefined, orgId: string | null | undefined): UnreadCount {
  return useUnreadCount(talentId && orgId ? { portal: "creator", orgId, talentId } : null);
}

/**
 * Creator bei mehreren Agenturen (Befund 7): Zahl je Agentur (offene Aufgaben, ungelesene Hinweise und Nachrichten)
 * für den Agenturwechsler, damit Nachrichten der anderen Agentur nicht unbemerkt liegen bleiben.
 */
export function useUnreadByOrg(enabled: boolean): Record<string, number> {
  const q = useQuery({
    queryKey: ["unread", "je-agentur", api.session()?.userId ?? null, "nachrichten"],
    enabled,
    queryFn: () => loadUnreadByOrg(api),
    refetchInterval: api.mode === "demo" ? false : 30_000,
  });
  return q.data ?? {};
}

// ── Desktop-Hinweis ─────────────────────────────────────────────────────────

/** Was der Browser erlaubt: „unsupported“ (kein Notification-API), sonst die Erlaubnis. */
export function desktopPermission(): "unsupported" | "default" | "granted" | "denied" {
  if (typeof window === "undefined" || typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

function useDesktopPreference(): boolean {
  const userId = api.session()?.userId ?? null;
  const q = useQuery({
    queryKey: ["user_preferences", userId, "desktop", "nachrichten"],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const rows = await api.select<{ desktop_notifications: boolean }>("user_preferences", { columns: "desktop_notifications", filters: [["user_id", "eq", userId!]] }).catch(() => []);
      return rows[0]?.desktop_notifications ?? true;
    },
  });
  return q.data ?? true;
}

function useDesktopAlerts(scope: UnreadScope | null, data: UnreadData | undefined) {
  const enabled = useDesktopPreference();
  const navigate = useNavigate();
  const seen = useRef<AlertState | null>(null);
  useEffect(() => {
    if (!scope || !data) return;
    const key = `${scope.portal}:${scope.orgId}:${scope.talentId ?? ""}`;
    const now: AlertState = { key, messages: data.count.messages, latest: data.latest[0]?.lastAt ?? null };
    const before = seen.current;
    seen.current = now;
    // Erster Stand nach dem Laden oder nach einem Agenturwechsel: nichts melden, nur merken
    if (!isNewMessage(before, now)) return;
    const top = data.latest[0]!;
    const ziel = scope.portal === "agentur" ? `/agentur/nachrichten/${top.talentId}` : "/creator/nachrichten";
    const sichtbar = document.visibilityState === "visible" && (typeof document.hasFocus !== "function" || document.hasFocus());
    if (sichtbar && fadenOffen(scope.portal, top.talentId, window.location.pathname)) return;
    if (!sichtbar && (!enabled || desktopPermission() !== "granted")) return;
    void (async () => {
      let von = "";
      if (scope.portal === "agentur") {
        const tl = await api.select<TalentLite>("talents", { columns: "id, first_name, last_name, display_name, status", filters: [["id", "eq", top.talentId]] }).catch(() => []);
        if (tl[0]) von = personName(tl[0]);
      } else {
        const o = await api.select<{ name: string }>("org_profiles", { columns: "name", filters: [["org_id", "eq", scope.orgId]] }).catch(() => []);
        if (o[0]) von = o[0].name;
      }
      if (sichtbar) {
        zeigeChatHinweis({ von: von || t("deinem Team", "your team"), text: alertExcerpt(top.lastBody), ziel, talentId: top.talentId });
        return;
      }
      const text = alertExcerpt(top.lastBody, 120);
      try {
        const n = new Notification(von ? t(`Neue Nachricht von ${von}`, `New message from ${von}`) : t("Neue Nachricht", "New message"),
          { body: text || t("Öffne CreatorOffice, um sie zu lesen.", "Open CreatorOffice to read it."), tag: "talentkontor-chat" });
        n.onclick = () => {
          window.focus();
          navigate(ziel);
          n.close();
        };
      } catch {
        /* manche Browser erlauben Hinweise nur aus einem Service Worker; dann bleibt es beim Zähler */
      }
    })();
    // scope ist je Aufruf ein neues Objekt; maßgeblich sind seine Werte
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope?.portal, scope?.orgId, scope?.talentId, data, enabled, navigate]);
}
